// Product API interfaces based on OpenAPI specification
export interface ProductImage {
  product_id: number;
  image_base64: string;
}

export interface ProductAttributeBlock {
  attribute_name: string;
  values: string[];
}

export interface ProductDetail {
  id: number;
  slug: string;
  name: string;
  brand?: string;
  ecommerce_sale_description?: string;
  ecommerce_short_description?: string;
  length?: number;
  width?: number;
  height?: number;
  attributes: ProductAttributeBlock[];
}

export interface ProductListItem {
  id: number;
  slug?: string;
  name: string;
  brand?: string;
  tags: string[];
  image_base64: string;
  short_description?: string;
  length?: number;
  width?: number;
  height?: number;
  price: number;
  currency: string;
  url?: string;
  category_id?: number;
  category_name?: string;
  sku?: string;
}

export interface SearchItem {
  id: number;
  slug?: string;
  name: string;
  brand?: string;
  image_base64: string;
  short_description?: string;
  length?: number;
  width?: number;
  height?: number;
  url?: string;
  category_id?: number;
  category_name?: string;
}

export interface SearchRequest {
  name?: string;
  categorie?: number | string;
  attributes?: Record<string, string[]>;
  page_number?: number;
  page_size?: number;
}

export interface SearchResponse {
  total: number;
  page_number: number;
  page_size: number;
  items: SearchItem[];
}

export interface FilterAttribute {
  attribute_name: string;
  values: string[];
}

export interface FiltersResponse {
  attributes: FilterAttribute[];
}

export interface ApiError {
  error: string;
}

class ProductsApiService {
  private readonly baseUrl = 'https://moncopieur.cloud-erp.ma';

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData: ApiError = await response.json().catch(() => ({
        error: `HTTP ${response.status}: ${response.statusText}`
      }));
      throw new Error(errorData.error);
    }
    return response.json();
  }

  /**
   * Get product main image as base64
   */
  async getProductImage(productId: number): Promise<ProductImage> {
    const response = await fetch(`${this.baseUrl}/api/product/image/${productId}`);
    return this.handleResponse<ProductImage>(response);
  }

  /**
   * Get product details by slug
   */
  async getProductBySlug(slug: string): Promise<ProductDetail> {
    const response = await fetch(`${this.baseUrl}/api/product/${slug}`);
    return this.handleResponse<ProductDetail>(response);
  }

  /**
   * Search products with filters and pagination
   */
  async searchProducts(searchRequest: SearchRequest): Promise<SearchResponse> {
    const response = await fetch(`${this.baseUrl}/api/products/search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(searchRequest),
    });
    return this.handleResponse<SearchResponse>(response);
  }

  /**
   * Get available filter attributes
   */
  async getFilters(): Promise<FiltersResponse> {
    const response = await fetch(`${this.baseUrl}/api/products/filters`);
    return this.handleResponse<FiltersResponse>(response);
  }

  /**
   * Download product technical sheet file (PDF)
   */
  async downloadTechnicalSheet(productId: number): Promise<Blob> {
    const response = await fetch(`${this.baseUrl}/api/product/${productId}/technical_sheet`);
    if (!response.ok) {
      let errorData: ApiError | undefined;
      try { errorData = await response.json(); } catch {}
      throw new Error(errorData?.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    return response.blob();
  }

  /**
   * Get top sold products
   */
  async getTopSoldProducts(limit: number = 8): Promise<{ items: ProductListItem[]; count: number; limit: number }> {
    const response = await fetch(`${this.baseUrl}/api/products/top-sold?limit=${limit}`);
    return this.handleResponse<{ items: ProductListItem[]; count: number; limit: number }>(response);
  }

  /**
   * Get newest products
   */
  async getNewestProducts(limit: number = 8): Promise<{ items: ProductListItem[]; count: number; limit: number }> {
    const response = await fetch(`${this.baseUrl}/api/products/new?limit=${limit}`);
    return this.handleResponse<{ items: ProductListItem[]; count: number; limit: number }>(response);
  }
}

// Export singleton instance
export const productsApi = new ProductsApiService();
export default productsApi;